import React, { useMemo, useEffect, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, LabelList
} from "recharts";
import ChartSummaryRows from "./ChartSummaryRows";


const COLOR_DEFAULT  = "var(--org-bg-color, #f6615f)";
const COLOR_TRAINING = "#ff9a98";
const COLOR_READING  = "#4a90d9";
const COLOR_YELLOW   = "#e6a817";
const COLOR_RED      = "#c0392b";
const COLOR_NEAR_MISS = "#4a90d9";
const COLOR_PREVENTION = "#6aaa2a";
const COLOR_DOUBT = "#3F3F3F";

const makeTooltip = ({ rows, usePayloadValues = false }) => {
  const Square = ({ color }) => (
    <span
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        background: color,
        marginRight: 8,
        border: "1px solid #ccc",
      }}
    />
  );

  return ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const rowsToRender = usePayloadValues
      ? payload
          .filter((p) => p && p.dataKey)
          .map((p) => ({ label: p.name, value: p.value, color: p.fill }))
      : rows;

    if (!rowsToRender?.length) return null;
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #999",
          borderRadius: 4,
          padding: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          fontSize: 13,
          color: "#222",
          minWidth: 170,
        }}
      >
        {rowsToRender.map((row, idx) => (
          <div
            key={row.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: idx === rows.length - 1 ? 0 : 4,
            }}
          >
            <Square color={row.color} />
            <span>{row.label}: {row.value}</span>
          </div>
        ))}
      </div>
    );
  };
};

// Pure helper — not a hook, safe to define outside component
const buildCounts = (records) => {
  const map = {};
  records.forEach((rec) => {
    const acNo = String(rec.action_card_number ?? "").trim();
    if (!acNo) return;
    map[acNo] = (map[acNo] ?? 0) + 1;
  });
  return map;
};

const normalizeIncidentType = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "null" ? "" : normalized;
};

const getIncidentTypeValue = (record) =>
  normalizeIncidentType(record?.real_incident_type || record?.read_incident_type);

const getIncidentTypeCount = (record) => {
  const parsed = Number(record?.real_incident_type_count);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return getIncidentTypeValue(record) ? 1 : 0;
};

const buildIncidentTypeCounts = (records, targetType) => {
  const normalizedTarget = normalizeIncidentType(targetType);
  const map = {};

  records.forEach((rec) => {
    if (getIncidentTypeValue(rec) !== normalizedTarget) {
      return;
    }

    const acNo = String(rec.action_card_number ?? "").trim();
    if (!acNo) return;

    map[acNo] = (map[acNo] ?? 0) + getIncidentTypeCount(rec);
  });

  return map;
};

const isTruthyFlag = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  return normalized === "1" || normalized === "true";
};

const buildReportingCategoryCounts = (records, fieldName) => {
  const map = {};

  records.forEach((rec) => {
    if (!isTruthyFlag(rec?.[fieldName])) {
      return;
    }

    const acNo = String(rec.action_card_number ?? "").trim();
    if (!acNo) return;

    map[acNo] = (map[acNo] ?? 0) + 1;
  });

  return map;
};

const RiskBarChart = ({
  defaultRecords  = [],
  reportingCategoryRecords = [],
  trainingRecords = [],
  readRecords     = [],
  showTraining    = false,
  showRead        = false,
  actionCards     = [],
  lang            = "en",
  organizationId,
  showIncidentYellow = true,
  showIncidentRed = true,
  showNearMiss = false,
  showPrevention = false,
  showDoubtAboutReporting = false,
  height = 600,
  onDataUpdate,
}) => {
  // ── Every hook called unconditionally at the top ──────────────────────────
  const cbRef = useRef(onDataUpdate);

  useEffect(() => {
    cbRef.current = onDataUpdate;
  }, [onDataUpdate]);

  const uniqueActionCards = useMemo(
    () => {
      const seen = new Set();
      return actionCards.filter((card) => {
        const key = String(card?.ac_no ?? "");
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    [actionCards]
  );

  const isGentofteLife = Number(organizationId) === 19;
  const hasIncidentTypeFilter = showIncidentYellow || showIncidentRed;
  const hasReportingCategoryFilter =
    showNearMiss || showPrevention || showDoubtAboutReporting;
  const showIncidentTypeBars =
    isGentofteLife && hasIncidentTypeFilter;
  const showReportingCategoryBars = hasReportingCategoryFilter;
  const hideDefaultBar = showIncidentTypeBars || showReportingCategoryBars;

  const defaultCounts = useMemo(
    () => buildCounts(defaultRecords),
    [defaultRecords]
  );

  const yellowCounts = useMemo(
    () => buildIncidentTypeCounts(defaultRecords, "yellow"),
    [defaultRecords]
  );

  const redCounts = useMemo(
    () => buildIncidentTypeCounts(defaultRecords, "red"),
    [defaultRecords]
  );

  const nearMissCounts = useMemo(
    () => buildReportingCategoryCounts(reportingCategoryRecords, "has_never_miss"),
    [reportingCategoryRecords]
  );

  const preventionCounts = useMemo(
    () => buildReportingCategoryCounts(reportingCategoryRecords, "has_prevention"),
    [reportingCategoryRecords]
  );

  const doubtCounts = useMemo(
    () => buildReportingCategoryCounts(reportingCategoryRecords, "has_doubt_about_reporting"),
    [reportingCategoryRecords]
  );

  const trainingCounts = useMemo(
    () => buildCounts(trainingRecords),
    [trainingRecords]
  );

  const readCounts = useMemo(
    () => buildCounts(readRecords),
    [readRecords]
  );

  const chartData = useMemo(
    () =>
      uniqueActionCards.map((card) => {
        const acNo  = String(card.ac_no);
        const label = lang === "dk" ? card.danish_short_name : card.english_short_name;
        return {
          category:     label,
          ac_no:        acNo,
          english_name: card.english_name,
          danish_name:  card.danish_name,
          default:      hideDefaultBar ? 0 : (defaultCounts[acNo] ?? 0),
          yellow:       showIncidentTypeBars ? (yellowCounts[acNo] ?? 0) : 0,
          red:          showIncidentTypeBars ? (redCounts[acNo] ?? 0) : 0,
          nearMiss:     showReportingCategoryBars && showNearMiss ? (nearMissCounts[acNo] ?? 0) : 0,
          prevention:   showReportingCategoryBars && showPrevention ? (preventionCounts[acNo] ?? 0) : 0,
          doubtAboutReporting:
            showReportingCategoryBars && showDoubtAboutReporting ? (doubtCounts[acNo] ?? 0) : 0,
          training:     trainingCounts[acNo] ?? 0,
          reading:      readCounts[acNo]     ?? 0,
        };
      }),
    [
      uniqueActionCards,
      defaultCounts,
      yellowCounts,
      redCounts,
      nearMissCounts,
      preventionCounts,
      doubtCounts,
      trainingCounts,
      readCounts,
      lang,
      hideDefaultBar,
      showIncidentTypeBars,
      showReportingCategoryBars,
      showNearMiss,
      showPrevention,
      showDoubtAboutReporting,
    ]
  );

  useEffect(() => {
    cbRef.current?.(chartData);
  }, [chartData]);
  // ─────────────────────────────────────────────────────────────────────────

  const defaultLabel  = lang === "dk" ? "Faktisk hændelse" : "Real incident";
  const trainingLabel = lang === "dk" ? "Træning" : "Training";
  const readingLabel  = lang === "dk" ? "Læsning" : "Reading";
  const yellowLabel = lang === "dk" ? "Gul" : "Yellow";
  const redLabel = lang === "dk" ? "Rød" : "Red";
  const legendDefault = lang === "dk" ? "Faktisk hændelse" : "Real incident";
  const legendTraining = lang === "dk" ? "Træning" : "Training";
  const legendReading = lang === "dk" ? "Læst" : "Read";
  const legendYellow = lang === "dk" ? "Gul" : "Yellow";
  const legendRed = lang === "dk" ? "Rød" : "Red";
  const nearMissLabel = lang === "dk" ? "Near miss" : "Near miss";
  const preventionLabel = lang === "dk" ? "Forebyggelse" : "Prevention";
  const doubtLabel = lang === "dk" ? "Tvivl om rapportering" : "Doubt about reporting";

  // Build bars array without any conditional hook calls
  const bars = [
    !hideDefaultBar ? (
      <Bar key="default" dataKey="default" fill={COLOR_DEFAULT} name={defaultLabel}>
        <LabelList dataKey="default" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#111" }} />
      </Bar>
    ) : null,
    showIncidentTypeBars && showIncidentYellow
      ? (
        <Bar key="yellow" dataKey="yellow" fill={COLOR_YELLOW} name={yellowLabel}>
          <LabelList dataKey="yellow" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
    showIncidentTypeBars && showIncidentRed
      ? (
        <Bar key="red" dataKey="red" fill={COLOR_RED} name={redLabel}>
          <LabelList dataKey="red" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
    showReportingCategoryBars && showNearMiss
      ? (
        <Bar key="nearMiss" dataKey="nearMiss" fill={COLOR_NEAR_MISS} name={nearMissLabel}>
          <LabelList dataKey="nearMiss" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
    showReportingCategoryBars && showPrevention
      ? (
        <Bar key="prevention" dataKey="prevention" fill={COLOR_PREVENTION} name={preventionLabel}>
          <LabelList dataKey="prevention" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
    showReportingCategoryBars && showDoubtAboutReporting
      ? (
        <Bar key="doubtAboutReporting" dataKey="doubtAboutReporting" fill={COLOR_DOUBT} name={doubtLabel}>
          <LabelList dataKey="doubtAboutReporting" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
    showTraining
      ? (
        <Bar key="training" dataKey="training" fill={COLOR_TRAINING} name={trainingLabel}>
          <LabelList dataKey="training" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
    showRead
      ? (
        <Bar key="reading" dataKey="reading" fill={COLOR_READING} name={readingLabel}>
          <LabelList dataKey="reading" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
  ];

  const totalIncidents = defaultRecords.length;
  const totalYellow = Object.values(yellowCounts).reduce((sum, value) => sum + Number(value ?? 0), 0);
  const totalRed = Object.values(redCounts).reduce((sum, value) => sum + Number(value ?? 0), 0);
  const totalNearMiss = Object.values(nearMissCounts).reduce((sum, value) => sum + Number(value ?? 0), 0);
  const totalPrevention = Object.values(preventionCounts).reduce((sum, value) => sum + Number(value ?? 0), 0);
  const totalDoubt = Object.values(doubtCounts).reduce((sum, value) => sum + Number(value ?? 0), 0);
  const totalTraining = trainingRecords.length;
  const totalReading = readRecords.length;

  const summaryRows = useMemo(() => {
    const rows = [];
    if (!hideDefaultBar) {
      rows.push({
        label: legendDefault,
        value: totalIncidents,
        color: COLOR_DEFAULT,
        show: true,
      });
    }
    if (showIncidentTypeBars && showIncidentYellow) {
      rows.push({ label: legendYellow, value: totalYellow, color: COLOR_YELLOW, show: true });
    }
    if (showIncidentTypeBars && showIncidentRed) {
      rows.push({ label: legendRed, value: totalRed, color: COLOR_RED, show: true });
    }
    if (showReportingCategoryBars && showNearMiss) {
      rows.push({ label: nearMissLabel, value: totalNearMiss, color: COLOR_NEAR_MISS, show: true });
    }
    if (showReportingCategoryBars && showPrevention) {
      rows.push({ label: preventionLabel, value: totalPrevention, color: COLOR_PREVENTION, show: true });
    }
    if (showReportingCategoryBars && showDoubtAboutReporting) {
      rows.push({ label: doubtLabel, value: totalDoubt, color: COLOR_DOUBT, show: true });
    }
    if (showTraining) {
      rows.push({ label: legendTraining, value: totalTraining, color: COLOR_TRAINING, show: true });
    }
    if (showRead) {
      rows.push({ label: legendReading, value: totalReading, color: COLOR_READING, show: true });
    }
    return rows;
  }, [
    hideDefaultBar,
    showIncidentTypeBars,
    showReportingCategoryBars,
    legendDefault,
    legendYellow,
    legendRed,
    nearMissLabel,
    preventionLabel,
    doubtLabel,
    legendTraining,
    legendReading,
    totalIncidents,
    totalYellow,
    totalRed,
    totalNearMiss,
    totalPrevention,
    totalDoubt,
    totalTraining,
    totalReading,
    showIncidentYellow,
    showIncidentRed,
    showNearMiss,
    showPrevention,
    showDoubtAboutReporting,
    showTraining,
    showRead,
  ]);

  const tooltipContent = useMemo(
    () =>
      makeTooltip({ rows: summaryRows, usePayloadValues: true }),
    [summaryRows]
  );

  return (
    <div style={{ width: "100%", height }}>
      <ChartSummaryRows rows={summaryRows} />

      <ResponsiveContainer width="100%" height="90%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
          <XAxis
            dataKey="category"
            interval={0}
            angle={-90}
            textAnchor="end"
            height={130}
            tick={{ fontSize: 11 }}
          />
          <YAxis allowDecimals={false} />
          <Tooltip content={tooltipContent} />
          <Legend />
          {bars.filter(Boolean)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RiskBarChart;
